import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Sparkles, X, Send, Minimize2, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AIAssistant = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Voice states
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [useBrowserSpeech, setUseBrowserSpeech] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize Web Speech API if available
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('[Voice] Browser transcript:', transcript);
        if (transcript && transcript.trim()) {
          setInput(transcript);
          sendMessage(transcript);
        }
        setIsRecording(false);
      };
      
      recognition.onerror = (event) => {
        console.error('[Voice] Browser speech error:', event.error);
        if (event.error !== 'aborted') {
          toast.error(`Voice recognition error: ${event.error}`);
        }
        setIsRecording(false);
      };
      
      recognition.onend = () => {
        setIsRecording(false);
      };
      
      recognitionRef.current = recognition;
      setUseBrowserSpeech(true);
      console.log('[Voice] Browser Speech API available');
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Auto-show assistant for new users on first login
    const hasSeenAssistant = localStorage.getItem('seen_assistant');
    if (!hasSeenAssistant && user) {
      setTimeout(() => {
        setIsOpen(true);
        getContextualHelp();
        localStorage.setItem('seen_assistant', 'true');
      }, 2000);
    }
  }, [user]);

  const getContextualHelp = async () => {
    try {
      const currentPage = location.pathname.replace('/', '') || 'dashboard';
      const response = await axios.post(
        `${API}/ai/assistant/help?token=${token}`,
        {
          context: {
            current_page: currentPage,
            is_new_user: !localStorage.getItem('seen_assistant')
          }
        }
      );

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: new Date()
        };
        setMessages([assistantMessage]);
        
        // Speak the welcome message if voice is enabled
        if (voiceEnabled) {
          await speakText(response.data.response);
        }
      }
    } catch (error) {
      console.error('Failed to get contextual help', error);
    }
  };

  // Get supported MIME type for audio recording (Safari/iPad compatibility)
  const getSupportedMimeType = () => {
    // Prioritize formats for Safari/iOS/iPad which typically support mp4/aac
    const types = [
      'audio/mp4',
      'audio/mp4;codecs=aac',
      'audio/aac',
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/wav',
      '' // Empty string = browser default
    ];
    
    console.log('[Voice Debug] Checking supported MIME types...');
    console.log('[Voice Debug] MediaRecorder available:', typeof MediaRecorder !== 'undefined');
    
    for (const type of types) {
      try {
        const supported = type === '' ? true : MediaRecorder.isTypeSupported(type);
        console.log(`[Voice Debug] ${type || '(default)'}: ${supported ? 'SUPPORTED' : 'not supported'}`);
        if (supported) {
          return type || undefined; // Return undefined for default
        }
      } catch (e) {
        console.log(`[Voice Debug] Error checking ${type}:`, e.message);
      }
    }
    return null;
  };

  const startRecording = async () => {
    try {
      console.log('[Voice Debug] Starting recording...');
      console.log('[Voice Debug] Browser:', navigator.userAgent);
      
      // Try browser's Web Speech API first (more reliable on mobile)
      if (useBrowserSpeech && recognitionRef.current) {
        console.log('[Voice] Using Browser Speech Recognition API');
        try {
          recognitionRef.current.start();
          setIsRecording(true);
          toast.info('Listening... Speak now');
          return;
        } catch (speechError) {
          console.log('[Voice] Browser speech failed, falling back to MediaRecorder:', speechError);
        }
      }
      
      // Check if MediaRecorder is supported
      if (typeof MediaRecorder === 'undefined') {
        console.error('[Voice Debug] MediaRecorder is NOT available');
        toast.error('Voice recording is not supported on this device/browser');
        return;
      }
      console.log('[Voice Debug] MediaRecorder is available');

      const mimeType = getSupportedMimeType();
      console.log('[Voice Debug] Selected MIME type:', mimeType || '(browser default)');
      
      if (mimeType === null) {
        toast.error('No supported audio format found. Please try a different browser.');
        return;
      }

      // Check microphone permissions first
      console.log('[Voice Debug] Requesting microphone access...');
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          } 
        });
        console.log('[Voice Debug] Microphone access granted');
        console.log('[Voice Debug] Audio tracks:', stream.getAudioTracks().map(t => ({
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState
        })));
      } catch (micError) {
        console.error('[Voice Debug] Microphone error:', micError.name, micError.message);
        throw micError;
      }
      
      // Create MediaRecorder with or without explicit MIME type
      let mediaRecorder;
      try {
        const options = mimeType ? { mimeType } : {};
        console.log('[Voice Debug] Creating MediaRecorder with options:', options);
        mediaRecorder = new MediaRecorder(stream, options);
        console.log('[Voice Debug] MediaRecorder created successfully');
        console.log('[Voice Debug] MediaRecorder state:', mediaRecorder.state);
        console.log('[Voice Debug] MediaRecorder mimeType:', mediaRecorder.mimeType);
      } catch (recorderError) {
        console.error('[Voice Debug] Failed to create MediaRecorder:', recorderError);
        // Try with no options as fallback
        console.log('[Voice Debug] Trying fallback with no MIME type specified...');
        try {
          mediaRecorder = new MediaRecorder(stream);
          console.log('[Voice Debug] Fallback MediaRecorder created, mimeType:', mediaRecorder.mimeType);
        } catch (fallbackError) {
          console.error('[Voice Debug] Fallback also failed:', fallbackError);
          stream.getTracks().forEach(track => track.stop());
          toast.error('Failed to initialize recording. This device may not support audio recording.');
          return;
        }
      }
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        console.log('[Voice Debug] Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        console.log('[Voice Debug] Recording stopped');
        console.log('[Voice Debug] Total chunks:', audioChunksRef.current.length);
        const totalSize = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);
        console.log('[Voice Debug] Total audio size:', totalSize, 'bytes');
        
        const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        console.log('[Voice Debug] Created blob:', audioBlob.size, 'bytes, type:', audioBlob.type);
        
        await processAudio(audioBlob, actualMimeType);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('[Voice Debug] MediaRecorder error:', event.error);
        toast.error('Recording error. Please try again.');
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      try {
        console.log('[Voice Debug] Starting MediaRecorder...');
        mediaRecorder.start(1000); // Collect data every second
        console.log('[Voice Debug] MediaRecorder started, state:', mediaRecorder.state);
      } catch (startError) {
        console.error('[Voice Debug] Failed to start MediaRecorder:', startError);
        stream.getTracks().forEach(track => track.stop());
        toast.error(`Failed to start recording: ${startError.message}`);
        return;
      }
      
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      toast.info('Listening...');
    } catch (error) {
      console.error('[Voice Debug] Recording failed:', error.name, error.message);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please enable microphone permissions in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Audio recording is not supported on this device.');
      } else {
        toast.error(`Recording failed: ${error.message}`);
      }
    }
  };

  const stopRecording = () => {
    console.log('[Voice Debug] Stop recording requested');
    
    // Stop browser speech recognition if active
    if (recognitionRef.current && isRecording) {
      try {
        recognitionRef.current.stop();
        console.log('[Voice] Browser speech recognition stopped');
      } catch (e) {
        console.log('[Voice] Could not stop browser speech:', e);
      }
    }
    
    // Stop MediaRecorder if active
    if (mediaRecorderRef.current && isRecording) {
      try {
        console.log('[Voice Debug] Current state:', mediaRecorderRef.current.state);
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
          console.log('[Voice Debug] Stop called successfully');
        } else {
          console.log('[Voice Debug] Not in recording state, skipping stop');
        }
      } catch (stopError) {
        console.error('[Voice Debug] Error stopping recorder:', stopError);
      }
    }
    setIsRecording(false);
  };

  const processAudio = async (audioBlob, mimeType = 'audio/webm') => {
    console.log('[Voice Debug] Processing audio...');
    console.log('[Voice Debug] Blob size:', audioBlob.size, 'type:', audioBlob.type);
    console.log('[Voice Debug] MIME type param:', mimeType);
    
    setLoading(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const audioBase64 = reader.result;
        console.log('[Voice Debug] Audio converted to base64, length:', audioBase64.length);
        
        try {
          // Transcribe audio - pass mimeType for backend to handle different formats
          console.log('[Voice Debug] Sending to transcription API...');
          const transcriptResponse = await axios.post(
            `${API}/ai/voice/transcribe?token=${token}`,
            { 
              audio: audioBase64,
              mime_type: mimeType 
            }
          );
          console.log('[Voice Debug] Transcription response:', transcriptResponse.data);
          
          if (transcriptResponse.data.success) {
            const transcript = transcriptResponse.data.transcript;
            if (transcript && transcript.trim()) {
              console.log('[Voice Debug] Transcript:', transcript);
              setInput(transcript);
              // Auto-send the transcribed message
              await sendMessage(transcript);
            } else {
              console.log('[Voice Debug] Empty transcript received');
              toast.warning('Could not understand audio. Please try again.');
            }
          } else {
            console.error('[Voice Debug] Transcription failed:', transcriptResponse.data.message);
            toast.error(transcriptResponse.data.message || 'Failed to transcribe audio');
          }
        } catch (apiError) {
          console.error('[Voice Debug] API error:', apiError);
          toast.error('Failed to process voice input. Please try again.');
        }
        setLoading(false);
      };
      
      reader.onerror = () => {
        console.error('[Voice Debug] FileReader error');
        toast.error('Failed to process audio file');
        setLoading(false);
      };
    } catch (error) {
      console.error('[Voice Debug] Process audio error:', error);
      toast.error('Failed to process voice input. Please try again.');
      setLoading(false);
    }
  };

  const speakText = async (text) => {
    try {
      setIsSpeaking(true);
      
      // Try backend TTS first
      try {
        const response = await axios.post(
          `${API}/ai/voice/synthesize?token=${token}`,
          { text }
        );
        
        if (response.data.success && response.data.audio) {
          // Play audio from backend
          if (audioRef.current) {
            audioRef.current.src = response.data.audio;
            await audioRef.current.play();
            return;
          }
        }
      } catch (backendError) {
        console.log('Backend TTS not available, using browser fallback');
      }
      
      // Fallback to browser's Web Speech API
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        
        // Try to get a good voice
        const voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(v => v.lang.startsWith('en') && v.name.includes('Google')) 
          || voices.find(v => v.lang.startsWith('en'))
          || voices[0];
        
        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }
        
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        window.speechSynthesis.speak(utterance);
        return;
      }
      
      // No TTS available
      console.log('No TTS available');
    } catch (error) {
      console.error('Failed to synthesize speech', error);
    } finally {
      setTimeout(() => setIsSpeaking(false), 1000);
    }
  };

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Check if this looks like a command (contains action words)
      const commandKeywords = ['create', 'schedule', 'send', 'change', 'update', 'add', 'set', 'call', 'email', 'whatsapp', 'task', 'meeting', 'reminder'];
      const lowerText = textToSend.toLowerCase();
      const isCommand = commandKeywords.some(kw => lowerText.includes(kw));
      
      if (isCommand) {
        // Process as voice command for action execution
        const commandResponse = await axios.post(
          `${API}/ai/assistant/voice-command?token=${token}`,
          {
            command: textToSend,
            contact_context: null // Could be enhanced to pass current contact if on detail page
          }
        );
        
        const assistantMessage = {
          role: 'assistant',
          content: commandResponse.data.response,
          action_type: commandResponse.data.action_type,
          executed: commandResponse.data.executed,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        // Show toast for executed actions
        if (commandResponse.data.executed) {
          toast.success(`Action completed: ${commandResponse.data.action_type}`);
        }
        
        // Speak response if voice is enabled
        if (voiceEnabled && !isSpeaking) {
          await speakText(commandResponse.data.response);
        }
      } else {
        // Regular contextual help
        const currentPage = location.pathname.replace('/', '') || 'dashboard';
        const response = await axios.post(
          `${API}/ai/assistant/help?token=${token}`,
          {
            context: {
              current_page: currentPage
            },
            question: textToSend
          }
        );

        if (response.data.success) {
          const assistantMessage = {
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, assistantMessage]);
          
          // Speak response if voice is enabled
          if (voiceEnabled && !isSpeaking) {
            await speakText(response.data.response);
          }
        }
      }
    } catch (error) {
      console.error('Failed to send message', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "Sorry, I'm having trouble right now. Please try again.",
          timestamp: new Date()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleVoice = () => {
    setVoiceEnabled(!voiceEnabled);
    if (isSpeaking && audioRef.current) {
      audioRef.current.pause();
      setIsSpeaking(false);
    }
    toast.info(voiceEnabled ? 'Voice disabled' : 'Voice enabled');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-accent text-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform duration-200 z-50"
        data-testid="ai-assistant-button"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 bg-accent text-white px-4 py-3 rounded-full shadow-lg cursor-pointer hover:scale-105 transition-transform duration-200 z-50 flex items-center gap-2"
      >
        <Sparkles className="w-5 h-5" />
        <span className="font-medium">AI Assistant</span>
        {isSpeaking && <Volume2 className="w-4 h-4 animate-pulse" />}
      </div>
    );
  }

  return (
    <>
      <audio ref={audioRef} onEnded={() => setIsSpeaking(false)} style={{ display: 'none' }} />
      
      <Card className="fixed bottom-6 right-6 w-96 h-[32rem] shadow-2xl z-50 flex flex-col border-accent border-2" data-testid="ai-assistant-panel">
        {/* Header */}
        <div className="bg-accent text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            <h3 className="font-semibold">AI Assistant</h3>
            {isSpeaking && <Volume2 className="w-4 h-4 animate-pulse" />}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleVoice}
              className="hover:bg-white/20 p-1 rounded"
              title={voiceEnabled ? "Disable voice" : "Enable voice"}
              data-testid="toggle-voice-button"
            >
              {voiceEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <button
              onClick={() => setIsMinimized(true)}
              className="hover:bg-white/20 p-1 rounded"
              data-testid="minimize-assistant"
            >
              <Minimize2 size={18} />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-1 rounded"
              data-testid="close-assistant"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  msg.role === 'user'
                    ? 'bg-accent text-white'
                    : 'bg-white border border-slate-200 text-slate-700'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading}
              variant={isRecording ? "destructive" : "outline"}
              size="icon"
              data-testid="voice-button"
              className={isRecording ? "animate-pulse" : ""}
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </Button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isRecording ? "Listening..." : "Ask me anything..."}
              disabled={loading || isRecording}
              data-testid="assistant-input"
              className="flex-1"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim() || isRecording}
              className="bg-accent hover:bg-accent/90"
              data-testid="send-message-button"
            >
              <Send size={18} />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            {voiceEnabled ? "🎙️ Voice enabled - Click mic to speak" : "Voice disabled"}
          </p>
        </div>
      </Card>
    </>
  );
};

export default AIAssistant;
